/**
 * Theme: toggles the `.dark` class on <html> and persists choice to
 * localStorage. Pair with the inline boot script in index.html that
 * sets the initial class synchronously before React mounts — prevents
 * the dark/light flash on initial load (per R22 audit, item 3).
 *
 * Choice is binary: 'light' | 'dark'. No 'system' option — UX wanted a
 * concrete day/night state, not a Monitor icon. First-time visitors get
 * 'light' as default (matches the public tacosai.com palette).
 *
 * The Sonner toast component reads theme via this hook so toasts respect
 * the same setting without per-call wiring.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'tacosai-admin-theme';

function readSavedTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'light';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'dark' ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

interface ThemeContextShape {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextShape | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readSavedTheme());

  // Apply + persist on every change.
  useEffect(() => {
    applyTheme(theme);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(() => {
    setThemeState((t) => (t === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggle }),
    [theme, setTheme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const v = useContext(ThemeContext);
  if (!v) throw new Error('useTheme must be inside <ThemeProvider>');
  return v;
}
